import { ReactNode, useState } from 'react'

import { useAtomValue } from 'jotai'
import { useNavigate } from 'react-router-dom'

import { useNotifications } from '@sequencehq/core-components'
import { useMutation } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'

import { invoicePaths } from 'Invoices/invoices.router'
import { useInvalidateInvoice } from 'Invoices/hooks/useInvalidateInvoice'
import {
  invoiceAccountingDateAtom,
  invoiceCustomerBillingAddressAtom,
  invoiceCustomerLegalCompanyNameAtom,
  invoiceCustomerTaxStatusAtom,
  invoiceHasNegativeTotalAtom,
  invoiceIdAtom,
  isSavePendingAtom,
  lineItemGroupsAtom
} from 'Invoices/atoms/invoiceEditing.atom'
import { InvoiceConfirmationModal } from 'Invoices/components/LegacyInvoiceModals'
import { SendInvoiceModalBody } from '../SendInvoiceModalBody'
import { useHumanReadableTotalAfterCreditGrants } from 'Invoices/hooks/useCreditGrantTotals'

type Props = {
  trigger: ({
    onClick,
    disabled
  }: {
    onClick: () => void
    disabled: boolean
  }) => ReactNode
  onSuccess?: () => void
  showViewButton?: boolean
}

export const FinalizeAndSendModal = ({
  trigger,
  onSuccess,
  showViewButton = true
}: Props) => {
  const navigate = useNavigate()
  const { displayNotification, displayPromiseNotification } = useNotifications()
  const invalidateInvoice = useInvalidateInvoice()

  const [showModal, setShowModal] = useState<boolean>(false)

  const invoiceId = useAtomValue(invoiceIdAtom)
  const invoiceAccountingDate = useAtomValue(invoiceAccountingDateAtom)
  const customerTaxStatus = useAtomValue(invoiceCustomerTaxStatusAtom)
  const customerBillingAddress = useAtomValue(invoiceCustomerBillingAddressAtom)
  const customerLegalName = useAtomValue(invoiceCustomerLegalCompanyNameAtom)
  const lineItemGroups = useAtomValue(lineItemGroupsAtom)
  const totalAfterCredit = useHumanReadableTotalAfterCreditGrants()
  const hasNegativeTotal = useAtomValue(invoiceHasNegativeTotalAtom)
  const isSavePending = useAtomValue(isSavePendingAtom)

  const taxCategoriesPresentOnInvoice = (lineItemGroups
    ?.map(group => group.taxCategory)
    .filter(Boolean) ?? []) as Array<{ id: string; name: string }>

  const {
    mutateAsync: finalizeAndSendMutate,
    isPending: finalizeAndSendIsPending
  } = useMutation(dashboard20240730Client.postFinalizeAndSendInvoice, {
    onSuccess: () => {
      onSuccess?.()
    }
  })

  const { mutateAsync: recalculateAsync } = useMutation(
    dashboard20240730Client.postRecalculateInvoice,
    {
      onSuccess: () => {
        invalidateInvoice()
      }
    }
  )

  const onConfirm = async () => {
    if (!invoiceId) return

    displayNotification('Sending invoice...', {
      type: 'loading',
      id: invoiceId,
      duration: Infinity
    })

    try {
      await finalizeAndSendMutate({ id: invoiceId })

      if (showViewButton) {
        const navigatePath = invoicePaths.edit(invoiceId)
        displayNotification('Invoice sent', {
          type: 'success',
          'data-testid': 'invoice.toast.sendSuccess',
          viewLabel: 'View invoice',
          onClickView: () => navigate(navigatePath),
          id: invoiceId
        })
      } else {
        displayNotification('Invoice sent', {
          type: 'success',
          id: invoiceId
        })
      }
    } catch {
      displayNotification('Unable to send invoice', {
        type: 'error',
        id: invoiceId
      })
    }
  }

  const recalculateInvoice = () => {
    if (!invoiceId) return

    // Return the promise so callers that await recalculation (the tax-rate
    // remediation step) don't advance before it resolves.
    return displayPromiseNotification(recalculateAsync({ id: invoiceId }), {
      loading: 'Recalculating invoice...',
      success: 'Invoice recalculated',
      error: 'Failed to recalculate invoice'
    })
  }

  return (
    <>
      {trigger({
        onClick: () => setShowModal(true),
        disabled: isSavePending || finalizeAndSendIsPending || hasNegativeTotal
      })}

      {showModal && (
        <InvoiceConfirmationModal
          onClose={() => setShowModal(false)}
          onConfirm={async () => {
            // Close immediately; progress and errors are tracked by the
            // send toast rather than the modal.
            setShowModal(false)
            void onConfirm()
          }}
          recalculateInvoice={recalculateInvoice}
          taxCategoriesOnInvoice={taxCategoriesPresentOnInvoice}
          customerTaxStatus={customerTaxStatus}
          customerCountry={customerBillingAddress?.country}
          customerState={customerBillingAddress?.state}
          invoiceAccountingDate={invoiceAccountingDate}
          blockedTitle="This invoice can't be sent"
          validationSummaryText="There are a few things to sort out before you can send this invoice."
          confirmationSummaryText={<SendInvoiceModalBody />}
          confirmTitleText={`Send invoice for ${totalAfterCredit} to ${customerLegalName}?`}
          confirmActionText="Send invoice"
          submitting={finalizeAndSendIsPending}
        />
      )}
    </>
  )
}
